import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Package, Monitor, AlignJustify, Globe, Users, BarChart3, PieChart, Sun, Moon, LayoutGrid, ChevronRight, ChevronDown, LogOut, Bot, Palette, Shield, X, Cpu, Rocket, GitBranch, Check, Lock, Settings, KeyRound, Database, Library, Network, Coins, ListTree, FlaskConical, LifeBuoy } from 'lucide-react';
import ChangePasswordModal from './auth/ChangePasswordModal';
import { setTheme } from '../redux/store';
import { selectTheme, selectCurrentOrg, selectBreadcrumbs, selectImageTierVisible } from '../redux/selectors';
import api, { logout, listBranches } from '../services/api';
import { getTokenPayload, hasPermission, canManageUsers, isSuperAdmin } from '../services/auth';
import { paths, TAB_ROUTE_MAP } from '../routes';
import { useBranchContext } from '../hooks/useBranchContext';

// BranchesPicker — header button + dropdown listing active branches.
// Click a branch row → navigate into that shadow app. "Manage details" → /branches page.
// branchCtx (from sessionStorage) is passed in; when present we color the button amber
// and show the branch name. The "main app" entry in the dropdown lets users hop back.
const BranchesPicker = ({ orgId, appId, branchCtx }) => {
    const navigate = useNavigate();
    // When in a shadow, list parent's branches (so the dropdown shows all siblings).
    const parentAppId = branchCtx?.parentAppId || appId;
    const [open, setOpen] = useState(false);
    const [branches, setBranches] = useState(null);
    const [loading, setLoading] = useState(false);
    const [parentName, setParentName] = useState(branchCtx?.parentAppName || '');
    const ref = useRef(null);

    // branchCtx now resolves asynchronously (server-derived), so parentAppId
    // starts as the shadow's own id and becomes the parent's a moment later.
    // Without this, a dropdown opened during that window cached the SHADOW's
    // branch list — always empty — and kept showing it. Drop the cache whenever
    // the app we are listing branches for changes.
    useEffect(() => {
        setBranches(null);
        setParentName(branchCtx?.parentAppName || '');
    }, [parentAppId, branchCtx?.parentAppName]);

    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    const handleOpen = async () => {
        setOpen(o => !o);
        if (branches) return;
        setLoading(true);
        try {
            const [branchList, aRes] = await Promise.all([
                listBranches(parentAppId),
                parentName ? Promise.resolve(null) : api.get(`/apps/${parentAppId}`),
            ]);
            setBranches(branchList.filter(b => b.status === 'ACTIVE'));
            if (aRes && aRes.data?.name) setParentName(aRes.data.name);
        } catch {
            setBranches([]);
        } finally {
            setLoading(false);
        }
    };

    // goMain / goBranch use a HARD reload via window.location, not react-router's
    // navigate(). Soft navigation keeps the Redux workflow/activities/fields slices
    // populated with the previous app's data, so the editor shows stale content
    // until the user manually refreshes. A hard reload remounts everything and
    // re-fetches against the new appId cleanly.
    const goMain = () => {
        sessionStorage.removeItem(`branch_ctx_${parentAppId}`);
        setOpen(false);
        window.location.href = `/orgs/${orgId}/apps/${parentAppId}`;
    };

    const goBranch = (b) => {
        sessionStorage.removeItem(`branch_ctx_${b.shadow_app_id}`);
        // Persist the branch context now (no react-router location.state after
        // a full page load). MainLayout reads this key on mount.
        sessionStorage.setItem(`branch_ctx_${b.shadow_app_id}`, JSON.stringify({
            branchName: b.name,
            parentAppId: parentAppId,
            parentAppName: parentName,
            orgId,
        }));
        setOpen(false);
        window.location.href = `/orgs/${orgId}/apps/${b.shadow_app_id}`;
    };

    const goDetails = () => {
        setOpen(false);
        navigate(`/orgs/${orgId}/apps/${parentAppId}/branches`);
    };

    return (
        <div className="relative" ref={ref}>
            {/* The label is ALWAYS a word, never a bare icon. An icon-only
                button meant main and branch looked the same at a glance, which
                is the whole thing this control exists to tell you. Colour is a
                second signal, not the only one: amber = you are editing a
                branch, neutral = main. */}
            <button
                onClick={handleOpen}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-white text-xs font-bold rounded-lg transition-all active:scale-95 max-w-[200px] ${
                    branchCtx
                        ? 'bg-amber-500/70 hover:bg-amber-500/90'
                        : 'bg-white/15 hover:bg-white/25 dark:bg-indigo-600/30 dark:hover:bg-indigo-600/50 grey:bg-white/15 grey:hover:bg-white/25'
                }`}
                data-tooltip-content={branchCtx
                    ? `Editing branch "${branchCtx.branchName}" of ${branchCtx.parentAppName || 'its parent app'} — switch branch`
                    : 'Editing the main app — switch branch'}
            >
                <GitBranch size={13} strokeWidth={2.5} className="flex-shrink-0" />
                <span className="truncate">{branchCtx ? branchCtx.branchName : 'Main'}</span>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-slate-800 grey:bg-zinc-50 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
                        <GitBranch size={13} className="text-indigo-500" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Branches</span>
                    </div>

                    {/* Main app row — hop back from a shadow */}
                    <button
                        onClick={goMain}
                        className={`w-full px-3 py-2 flex items-center gap-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${!branchCtx ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{parentName || 'Main'}</div>
                            <div className="text-[10px] text-slate-400">main</div>
                        </div>
                        {!branchCtx && <Check size={13} className="text-indigo-500 flex-shrink-0" />}
                    </button>

                    {loading ? (
                        <div className="p-4 text-xs text-slate-400 text-center">Loading…</div>
                    ) : branches && branches.length === 0 ? (
                        <div className="px-3 py-3 text-[10px] text-slate-400 text-center italic border-t border-slate-100 dark:border-slate-700">No active branches</div>
                    ) : branches ? (
                        <div className="max-h-72 overflow-y-auto border-t border-slate-100 dark:border-slate-700">
                            {branches.map(b => {
                                const isActive = branchCtx && String(b.shadow_app_id) === String(appId);
                                return (
                                    <button
                                        key={b.id}
                                        onClick={() => goBranch(b)}
                                        className={`w-full px-3 py-2 flex items-center gap-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${isActive ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{b.name}</div>
                                            {b.description && <div className="text-[10px] text-slate-400 truncate">{b.description}</div>}
                                        </div>
                                        {isActive && <Check size={13} className="text-amber-600 flex-shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}

                    <button
                        onClick={goDetails}
                        className="w-full px-3 py-2 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-700 border-t border-slate-100 dark:border-slate-700 transition-colors text-center"
                    >
                        Manage branches →
                    </button>
                </div>
            )}
        </div>
    );
};

// LocksPicker — header button + dropdown showing a summary of component locks
// on the (parent) main app. "Manage locks" → /locks page. Read-only here; locks
// can only be modified from the dedicated /locks page or the per-component toggles.
const LocksPicker = ({ orgId, appId, branchCtx }) => {
    const navigate = useNavigate();
    // When in a shadow, list parent's locks transparently (the API does this for us
    // when given a shadow's appId, but we use the parent for the "Manage" link).
    const parentAppId = branchCtx?.parentAppId || appId;
    const [open, setOpen] = useState(false);
    const [locks, setLocks] = useState(null);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    const handleOpen = async () => {
        setOpen(o => !o);
        if (locks) return;
        setLoading(true);
        try {
            const res = await api.get(`/apps/${parentAppId}/locks`);
            setLocks(res.data?.locks || []);
        } catch {
            setLocks([]);
        } finally {
            setLoading(false);
        }
    };

    const goManage = () => {
        setOpen(false);
        navigate(`/orgs/${orgId}/apps/${parentAppId}/locks`);
    };

    // Group by component_type for the summary view
    const summary = useMemo(() => {
        if (!locks) return null;
        const byType = {};
        let frozen = 0;
        for (const l of locks) {
            byType[l.component_type] = (byType[l.component_type] || 0) + 1;
            if (l.scope === 'all') frozen++;
        }
        return { byType, frozen, total: locks.length };
    }, [locks]);

    const hasAny = summary && summary.total > 0;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={handleOpen}
                className="p-1.5 bg-white/15 hover:bg-white/25 dark:bg-indigo-600/30 dark:hover:bg-indigo-600/50 grey:bg-white/15 grey:hover:bg-white/25 text-white rounded-lg transition-all active:scale-95 relative"
                data-tooltip-content="Component Locks"
            >
                <Lock size={16} strokeWidth={2.5} />
                {hasAny && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-amber-500 text-white text-[8px] font-bold flex items-center justify-center">
                        {summary.total}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-slate-800 grey:bg-zinc-50 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
                        <Lock size={13} className="text-amber-500" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Locks</span>
                        {summary && (
                            <span className="ml-auto text-[10px] text-slate-400">
                                {summary.total} locked{summary.frozen > 0 ? ` · ${summary.frozen} frozen` : ''}
                            </span>
                        )}
                    </div>
                    {loading ? (
                        <div className="p-4 text-xs text-slate-400 text-center">Loading…</div>
                    ) : !hasAny ? (
                        <div className="px-3 py-4 text-[11px] text-slate-400 text-center italic">
                            No components are locked.
                        </div>
                    ) : (
                        <div className="max-h-72 overflow-y-auto py-1">
                            {Object.entries(summary.byType).sort(([a], [b]) => a.localeCompare(b)).map(([type, count]) => (
                                <div key={type} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                    <span className="font-mono text-[10px] text-slate-600 dark:text-slate-300">{type}</span>
                                    <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded">
                                        {count}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    <button
                        onClick={goManage}
                        className="w-full px-3 py-2 text-xs font-bold text-amber-600 dark:text-amber-400 hover:bg-slate-50 dark:hover:bg-slate-700 border-t border-slate-100 dark:border-slate-700 transition-colors text-center"
                    >
                        Manage locks →
                    </button>
                </div>
            )}
        </div>
    );
};

// AppThemePicker — navigates to the full per-device theme assignment page.
const AppThemePicker = ({ orgId, appId }) => {
    const navigate = useNavigate();
    if (!orgId || !appId) return null; // no app context, nothing to assign

    return (
        <button
            onClick={() => navigate(`/orgs/${orgId}/apps/${appId}/theme`)}
            className="p-1.5 bg-white/15 hover:bg-white/25 dark:bg-indigo-600/30 dark:hover:bg-indigo-600/50 grey:bg-white/15 grey:hover:bg-white/25 text-white rounded-lg transition-all active:scale-95"
            data-tooltip-content="App Theme"
        >
            <Palette size={16} strokeWidth={2.5} />
        </button>
    );
};

const PERM_MODULES = [
    { id: 'apps', label: 'Apps' },
    { id: 'agents', label: 'Agents' },
    { id: 'pipelines', label: 'Pipelines' },
    { id: 'database', label: 'Database' },
    { id: 'config', label: 'Config' },
    { id: 'roles', label: 'Roles' },
    { id: 'groups', label: 'Groups' },
    { id: 'users', label: 'App Users' },
    { id: 'monitoring', label: 'Monitoring', readOnly: true },
    { id: 'analytics', label: 'Analytics' },
    { id: 'ai_cost', label: 'AI Cost' },
];

// The switcher's module order MUST match the org dashboard's tab order —
// AppDashboard.js's ALL_TABS. The two are separate lists (that one is bare ids
// for the tab strip, this one carries labels/icons/routes/tier flags), so
// reordering one means reordering the other. 'coding-agent' is the single
// deliberate difference: it is a standalone route with no dashboard tab.
const MODULE_LIST = [
    { id: 'apps', label: 'Apps', icon: <Package size={16} strokeWidth={2} /> },
    // Both tiers. Authoring is still image-tier and locked by the route guard;
    // what a consumer target gets instead is the READ-ONLY deployed roster
    // (DeployedAgentsPage / DeployedAIEmployeesPage). Hiding these entries left
    // those pages reachable only by typed URL, which is the same dead end the
    // deploy page had.
    { id: 'agents', label: 'Agents', icon: <Monitor size={16} strokeWidth={2} /> },
    { id: 'ai-employees', label: 'AI Employees', icon: <Bot size={16} strokeWidth={2} /> },
    { id: 'pipelines', label: 'Pipelines', icon: <AlignJustify size={16} strokeWidth={2} /> },
    { id: 'analytics', label: 'Analytics', icon: <PieChart size={16} strokeWidth={2} />, imageTier: true },
    { id: 'user-management', label: 'Access', route: 'access', icon: <Users size={16} strokeWidth={2} /> },
    { id: 'interfaces', label: 'Interfaces', route: 'interfaces', icon: <Globe size={16} strokeWidth={2} /> },
    // No imageTier/consumerOnly flag — a prod/target operator is the one
    // person who MUST be able to fill a `per_env` slot after release, so
    // this stays visible (and its route unguarded) on every target.
    { id: 'config-variables', label: 'Config Variables', route: 'config-variables', icon: <Settings size={16} strokeWidth={2} /> },
    { id: 'coding-agent', label: 'Coding Agent', route: 'coding-agent', icon: <Cpu size={16} strokeWidth={2} />, imageTier: true },
    { id: 'context', label: 'Context', route: 'context', icon: <Network size={16} strokeWidth={2} /> },
    // Both tiers: a target needs it because books are env-specific while agent
    // shells ship with the release, and a builder needs a way in that does not
    // go through Agents > RAG Services. Matches the dashboard tab gate.
    { id: 'knowledge-bases', label: 'Knowledge Bases', icon: <Library size={16} strokeWidth={2} /> },
    { id: 'database', label: 'Database', route: 'database', icon: <Database size={16} strokeWidth={2} />, consumerOnly: true },
    { id: 'monitoring', label: 'Monitoring', route: 'monitor', icon: <BarChart3 size={16} strokeWidth={2} /> },
    { id: 'ai_cost', label: 'AI Cost', route: 'ai-cost', icon: <Coins size={16} strokeWidth={2} /> },
    // Requirements + QA are org-level and stay visible on a consumer target:
    // a target operator needs to read what was specified and run the read-only
    // suite against it, even though they cannot author the app there.
    { id: 'requirements', label: 'Requirements', route: 'requirements', icon: <ListTree size={16} strokeWidth={2} /> },
    // Support desks are org-level and stay visible on a consumer target: a
    // target operator is exactly the person fielding "why is this broken".
    { id: 'support', label: 'Support', route: 'support', icon: <LifeBuoy size={16} strokeWidth={2} /> },
    { id: 'releases', label: 'Releases', route: 'releases', icon: <Rocket size={16} strokeWidth={2} /> },
    { id: 'qa', label: 'QA', route: 'qa', icon: <FlaskConical size={16} strokeWidth={2} /> },
];
// imageTier: true marks an authoring-only module, hidden from the switcher on
// a consumer target. apps/user-management/monitoring/releases stay:
// per-target settings, org-local RBAC, read-only monitoring, and
// releases (the target's job). 'interfaces' also stays reachable — it shows
// its own "not available on this target" panel (APICatalogStudio's
// tabLocked) for every one of its remaining tabs, rather than 404ing or
// relying on ConsumerImageGuard. The org config-vars tab that used to live
// there (the actual reason this entry stayed visible) is now its own
// 'config-variables' entry below, which genuinely does stay editable on a
// target — a prod/target operator filling a `per_env` slot is the point.
// consumerOnly: true is the inverse — shown only on a consumer target, matching
// the dashboard tab gate.

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentOrg = useSelector(selectCurrentOrg);
    const breadcrumbs = useSelector(selectBreadcrumbs);
    const imageTierVisible = useSelector(selectImageTierVisible);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showModules, setShowModules] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const modulesRef = useRef(null);

    // Extract appId from URL when inside an app context
    const appId = useMemo(() => {
        const match = location.pathname.match(/\/orgs\/[^/]+\/apps\/([^/]+)/);
        return match ? match[1] : null;
    }, [location.pathname]);

    // Extract orgId from URL for branch navigation
    const orgIdFromURL = useMemo(() => {
        const match = location.pathname.match(/\/orgs\/([^/]+)/);
        return match ? match[1] : null;
    }, [location.pathname]);

    // Branch context — whether this appId is a shadow app, and of what.
    //
    // Resolved from the server, not from sessionStorage. The old version read
    // the breadcrumb inside a useMemo keyed on [appId]: on a soft navigation
    // carrying location.state.branchContext (BranchesPage, BranchPickerModal),
    // Header rendered and memoised `null` BEFORE MainLayout wrote the key —
    // which it does only after awaiting fetchOrgs + fetchApps. appId never
    // changed again, so the memo never re-ran and the header claimed "main" on
    // a branch until a hard reload. That is the timing bug; goBranch only
    // avoided it by doing a full page load.
    const { branchCtx } = useBranchContext(appId);

    // Derive scope from JWT for the permissions modal
    const scopeData = useMemo(() => {
        const payload = getTokenPayload();
        const scope = payload?.scope || {};
        const allowedOrgIds = scope.org_ids !== undefined ? scope.org_ids : null;
        const allowedAppIds = scope.app_ids !== undefined ? scope.app_ids : null;
        const scopeTier = (allowedOrgIds === null || scope.full_access) ? 'full' : allowedAppIds === null ? 'org' : 'app';
        return { allowedOrgIds, allowedAppIds, scopeTier };
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const activeModule = useMemo(() => {
        const segments = location.pathname.split('/').filter(Boolean);
        let afterOrg = segments.length >= 3 ? segments[2] : '';
        // Map sub-pages to their parent module
        const MODULE_ALIASES = { 'ai-playground': 'apps', 'import-app': 'apps' };
        if (MODULE_ALIASES[afterOrg]) afterOrg = MODULE_ALIASES[afterOrg];
        const match = MODULE_LIST.find(mod => {
            const route = mod.route || TAB_ROUTE_MAP[mod.id] || mod.id;
            return afterOrg === route || afterOrg.startsWith(route)
                || afterOrg === mod.id || afterOrg.startsWith(mod.id);
        });
        return match || null;
    }, [location.pathname]);

    // Close modules dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modulesRef.current && !modulesRef.current.contains(e.target)) {
                setShowModules(false);
            }
        };
        if (showModules) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showModules]);

    const handleModuleClick = (mod) => {
        if (!currentOrg?.id) return;
        const orgId = currentOrg.id;
        if (mod.route) {
            navigate(paths.orgTab(orgId, mod.route));
        } else {
            const segment = TAB_ROUTE_MAP[mod.id] || mod.id;
            navigate(paths.orgTab(orgId, segment));
        }
        setShowModules(false);
    };


    return (
        <header id="top-navigation" data-section="top-navbar" className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 grey:from-indigo-600 grey:via-purple-600 grey:to-indigo-700 border-b border-indigo-700 dark:border-slate-800 grey:border-indigo-700 h-10 flex items-center justify-between px-6 shadow-lg z-50 sticky top-0 transition-colors duration-300">
            <div className="flex items-center gap-4">
                <Link to="/" className="flex items-center gap-2.5 group no-underline">
                    <img src="/zino.svg" alt="Zino" className="h-5 w-auto brightness-0 invert group-hover:scale-105 transition-all duration-300" />
                </Link>

                {/* Module Switcher - left of breadcrumbs */}
                {currentOrg?.id && (
                    <div className="relative" ref={modulesRef}>
                        <button
                            onClick={() => { setShowModules(!showModules); setShowDropdown(false); }}
                            className={`p-1 rounded-lg transition-all flex items-center gap-2 ${showModules ? 'text-white bg-white/25 dark:text-indigo-400 dark:bg-slate-700' : 'text-white/80 dark:text-slate-400 grey:text-white/80 hover:text-white dark:hover:text-indigo-400 grey:hover:text-white hover:bg-white/20 dark:hover:bg-slate-700 grey:hover:bg-white/20'}`}
                            data-tooltip-content="Switch Module"
                        >
                            <LayoutGrid size={20} strokeWidth={2} />
                            {activeModule && (
                                <span className="text-[12px] font-bold uppercase tracking-widest text-white/90 dark:text-slate-300 grey:text-white/90 pr-1">
                                    {activeModule.label}
                                </span>
                            )}
                        </button>

                        {showModules && (
                            <div className="absolute top-full left-0 mt-1.5 w-52 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-100 dark:border-slate-700 py-1.5 z-50">
                                <div className="px-3 py-1.5 mb-1">
                                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">Switch Module</span>
                                </div>
                                {MODULE_LIST.filter(mod => {
                                    if (mod.imageTier && !imageTierVisible) return false;
                                    if (mod.consumerOnly && imageTierVisible) return false;
                                    const permKey = mod.id === 'interfaces' ? 'agents'
                                        : mod.id === 'config-variables' ? 'config'
                                        : mod.id === 'knowledge-bases' ? 'agents'
                                        : mod.id === 'user-management' ? 'users'
                                        : mod.id === 'ai-employees' ? 'apps'
                                        : mod.id === 'releases' ? 'apps'
                                        // Context has no permission module of its own; its
                                        // routes are guarded by rp("apps", ...) server-side
                                        // and <PrivateRoute module="apps"> on the client.
                                        // Without this it falls through to hasPermission('context')
                                        // and silently vanishes from the switcher.
                                        : mod.id === 'context' ? 'apps'
                                        : mod.id;
                                    return hasPermission(permKey, 'read');
                                }).map(mod => (
                                    <button
                                        key={mod.id}
                                        onClick={() => handleModuleClick(mod)}
                                        className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 font-bold flex items-center gap-2.5 transition-colors"
                                    >
                                        <span className="text-slate-400 dark:text-slate-500">{mod.icon}</span>
                                        {mod.id === 'interfaces' && !imageTierVisible ? 'Environment Setup' : mod.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {breadcrumbs && breadcrumbs.length > 0 && (
                    <>
                        <div className="h-4 w-px bg-white/30 dark:bg-slate-700 grey:bg-white/30"></div>
                        <div className="flex items-center gap-1 overflow-hidden">
                            {breadcrumbs.map((crumb, idx) => (
                                <React.Fragment key={idx}>
                                    {idx > 0 && (
                                        <ChevronRight size={12} strokeWidth={3} className="text-white/40 dark:text-slate-500 grey:text-white/40 flex-shrink-0" />
                                    )}
                                    {crumb.path ? (
                                        <Link to={crumb.path} className="text-xs font-bold text-white/70 dark:text-slate-400 grey:text-white/70 hover:text-white dark:hover:text-indigo-400 grey:hover:text-white truncate transition-colors no-underline">
                                            {crumb.label}
                                        </Link>
                                    ) : (
                                        <span className="text-xs font-bold text-white/90 dark:text-slate-200 grey:text-white/90 truncate">
                                            {crumb.label}
                                        </span>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <div className="flex items-center gap-3">
                {/* Branches, Locks, Theme, Deploy — hidden on consumer targets */}
                {imageTierVisible && appId && orgIdFromURL && (
                    <BranchesPicker orgId={orgIdFromURL} appId={appId} branchCtx={branchCtx} />
                )}

                {imageTierVisible && appId && orgIdFromURL && (
                    <LocksPicker orgId={orgIdFromURL} appId={appId} branchCtx={branchCtx} />
                )}

                {imageTierVisible && appId && orgIdFromURL && (
                    <AppThemePicker orgId={orgIdFromURL} appId={appId} />
                )}

                {imageTierVisible && appId && orgIdFromURL && (
                    <button
                        onClick={() => navigate(`/orgs/${orgIdFromURL}/apps/${appId}/deploy`)}
                        className="p-1.5 bg-white/15 hover:bg-white/25 dark:bg-indigo-600/30 dark:hover:bg-indigo-600/50 grey:bg-white/15 grey:hover:bg-white/25 text-white rounded-lg transition-all active:scale-95"
                        data-tooltip-content="Deploy App"
                    >
                        <Rocket size={16} strokeWidth={2.5} />
                    </button>
                )}

                {/* Consumer target: the rocket above is image-tier and hidden, and
                    so is the apps-list kebab that also links to Deploy — which left
                    the deployed-overview page (not image-tier, and the only way to
                    inspect what the target is running) reachable by typed URL only.
                    This takes its place, so the header always offers whichever of
                    the two the current target can actually use. */}
                {!imageTierVisible && appId && orgIdFromURL && (
                    <button
                        onClick={() => navigate(paths.deployedOverview(orgIdFromURL, appId))}
                        className="p-1.5 bg-white/15 hover:bg-white/25 dark:bg-indigo-600/30 dark:hover:bg-indigo-600/50 grey:bg-white/15 grey:hover:bg-white/25 text-white rounded-lg transition-all active:scale-95"
                        data-tooltip-content="Deployed App Detail"
                    >
                        <FlaskConical size={16} strokeWidth={2.5} />
                    </button>
                )}

                <div className="hidden sm:block h-4 w-px bg-white/30 dark:bg-slate-700 mx-1"></div>

                <UserProfileMenu showDropdown={showDropdown} setShowDropdown={setShowDropdown} setShowModules={setShowModules} setShowProfile={setShowProfile} />

                {/* My Permissions Modal */}
                {showProfile && (
                    <PermissionsModal
                        onClose={() => setShowProfile(false)}
                        scopeTier={scopeData.scopeTier}
                        allowedOrgIds={scopeData.allowedOrgIds}
                        allowedAppIds={scopeData.allowedAppIds}
                    />
                )}

            </div>
        </header>
    );
};

// ─── User Profile Menu ──────────────────────────────────────────────────────

const UserProfileMenu = ({ showDropdown, setShowDropdown, setShowModules, setShowProfile }) => {
    const profileRef = useRef(null);
    const tokenData = useMemo(() => getTokenPayload(), []);
    const name = tokenData?.name || '';
    const email = tokenData?.email || '';
    const dispatch = useDispatch();
    const theme = useSelector(selectTheme);
    // Global theme management — hidden on consumer targets.
    const imageTierVisible = useSelector(selectImageTierVisible);
    const [showChangePw, setShowChangePw] = useState(false);

    const THEMES = [
        { key: 'light', label: 'Light', Icon: Sun },
        { key: 'grey', label: 'Grey', Icon: Monitor },
        { key: 'dark', label: 'Dark', Icon: Moon },
    ];

    const label = name || email?.split('@')[0] || 'User';
    const initials = label.split(/[\s._-]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

    // Close dropdown on outside click
    useEffect(() => {
        if (!showDropdown) return;
        const handler = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showDropdown, setShowDropdown]);

    return (
        <div
            ref={profileRef}
            className="flex items-center gap-2 pl-2 pr-1 py-0.5 rounded-lg hover:bg-white/10 dark:hover:bg-slate-700 grey:hover:bg-white/10 cursor-pointer transition-all group relative shrink-0"
            onClick={() => { setShowDropdown(!showDropdown); setShowModules(false); }}
        >
            <div className="hidden sm:flex flex-col items-end">
                <span className="text-[12px] font-bold text-white/90 dark:text-slate-300 grey:text-white/90 group-hover:text-white dark:group-hover:text-white transition-colors leading-none">
                    {label}
                </span>
                {email && (
                    <span className="text-[10px] text-white/50 dark:text-slate-500 grey:text-white/50 leading-none mt-0.5">
                        {email}
                    </span>
                )}
            </div>
            <div className="w-7 h-7 rounded-full bg-white/20 dark:bg-slate-700 grey:bg-white/20 flex items-center justify-center text-white text-[12px] font-bold shadow-sm">
                {initials || 'U'}
            </div>
            <ChevronDown size={12} strokeWidth={2.5} className={`text-white/60 dark:text-slate-500 grey:text-white/60 group-hover:text-white dark:group-hover:text-slate-300 transition-all duration-200 ${showDropdown ? 'rotate-180' : ''}`} />

            {/* Always rendered so open/close both animate; visibility toggled by classes */}
            <div
                onClick={(e) => e.stopPropagation()}
                className={`absolute top-full right-0 mt-2 w-60 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl shadow-slate-900/20 dark:shadow-black/50 border border-slate-100 dark:border-slate-700 z-50 overflow-hidden origin-top-right transition-all duration-200 ease-out ${
                    showDropdown
                        ? 'opacity-100 scale-100 translate-y-0 visible pointer-events-auto'
                        : 'opacity-0 scale-95 -translate-y-1 invisible pointer-events-none'
                }`}
            >
                {/* User header */}
                <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white text-[13px] font-bold shadow-sm flex-shrink-0">
                        {initials || 'U'}
                    </div>
                    <div className="min-w-0">
                        <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate leading-tight">{label}</p>
                        {email && <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate leading-tight mt-0.5">{email}</p>}
                    </div>
                </div>
                {/* Theme switcher */}
                <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-700">
                    <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">Builder Theme</div>
                    <div className="flex items-center gap-1.5">
                        {THEMES.map(({ key, label: tlabel, Icon }) => {
                            const active = theme === key;
                            return (
                                <button
                                    key={key}
                                    onClick={(e) => { e.stopPropagation(); dispatch(setTheme(key)); }}
                                    className={`flex-1 flex flex-col items-center gap-1 py-1.5 rounded-lg transition-all ${
                                        active
                                            ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 ring-1 ring-indigo-400 dark:ring-indigo-500'
                                            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                                    }`}
                                    data-tooltip-content={tlabel}
                                >
                                    <Icon size={14} strokeWidth={2} />
                                    <span className="text-[10px] font-bold">{tlabel}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="p-1.5">
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowDropdown(false); if (setShowProfile) setShowProfile(true); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 font-medium flex items-center gap-2.5 transition-colors"
                    >
                        <Shield size={14} strokeWidth={2} className="text-slate-400 dark:text-slate-500" />
                        My Permissions
                    </button>
                    {canManageUsers() && (
                        <Link
                            to="/builder-users"
                            className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 font-medium flex items-center gap-2.5 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Users size={14} strokeWidth={2} className="text-slate-400 dark:text-slate-500" />
                            Team Members
                        </Link>
                    )}
                    {canManageUsers() && (
                        <Link
                            to="/users"
                            className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 font-medium flex items-center gap-2.5 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Users size={14} strokeWidth={2} className="text-slate-400 dark:text-slate-500" />
                            App Users
                        </Link>
                    )}
                    {imageTierVisible && (
                    <Link
                        to="/themes"
                        className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 font-medium flex items-center gap-2.5 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Palette size={14} strokeWidth={2} className="text-slate-400 dark:text-slate-500" />
                        Manage Themes
                    </Link>
                    )}
                    {canManageUsers() && (
                        <Link
                            to={paths.customDomains()}
                            className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 font-medium flex items-center gap-2.5 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Globe size={14} strokeWidth={2} className="text-slate-400 dark:text-slate-500" />
                            Custom Domains
                        </Link>
                    )}
                    <Link
                        to={paths.llmConfig()}
                        className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 font-medium flex items-center gap-2.5 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Cpu size={14} strokeWidth={2} className="text-slate-400 dark:text-slate-500" />
                        AI Provider Settings
                    </Link>
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowDropdown(false); setShowChangePw(true); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 font-medium flex items-center gap-2.5 transition-colors"
                    >
                        <KeyRound size={14} strokeWidth={2} className="text-slate-400 dark:text-slate-500" />
                        Change Password
                    </button>
                    {isSuperAdmin() && (
                        <Link
                            to={paths.generalSettings()}
                            className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/70 font-medium flex items-center gap-2.5 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Settings size={14} strokeWidth={2} className="text-slate-400 dark:text-slate-500" />
                            General Settings
                        </Link>
                    )}
                </div>
                <div className="p-1.5 border-t border-slate-100 dark:border-slate-700">
                    <button
                        onClick={(e) => { e.stopPropagation(); logout(); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium flex items-center gap-2.5 transition-colors"
                    >
                        <LogOut size={14} strokeWidth={2} />
                        Sign Out
                    </button>
                </div>
            </div>
            <ChangePasswordModal open={showChangePw} onClose={() => setShowChangePw(false)} />
        </div>
    );
};

// ─── Permissions Modal ──────────────────────────────────────────────────────

const PermissionsModal = ({ onClose, scopeTier, allowedOrgIds, allowedAppIds }) => {
    const tokenData = useMemo(() => getTokenPayload(), []);
    const name = tokenData?.name || '';
    const email = tokenData?.email || '';
    const role = tokenData?.role || '';
    const label = name || email?.split('@')[0] || 'User';
    const initials = label.split(/[\s._-]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

    const perms = useMemo(() => {
        const p = tokenData?.permissions || {};
        const result = {};
        Object.entries(p).forEach(([k, v]) => {
            result[k.startsWith('perm_') ? k : `perm_${k}`] = v;
        });
        return result;
    }, [tokenData]);

    const roleColors = { super_admin: 'from-indigo-600 to-violet-700', admin: 'from-violet-500 to-purple-600', member: 'from-blue-500 to-cyan-600', viewer: 'from-slate-500 to-slate-600' };
    const roleBadgeColors = { super_admin: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300', admin: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300', member: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', viewer: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' };
    const roleLabels = { super_admin: 'Super Admin', admin: 'Admin', member: 'Member', viewer: 'Viewer' };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header with gradient */}
                <div className={`relative bg-gradient-to-r ${roleColors[role] || roleColors.member} px-6 py-5`}>
                    <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 transition-colors">
                        <X size={14} />
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-bold shadow-lg border border-white/20">
                            {initials || 'U'}
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-white">{label}</h3>
                            {email && <p className="text-xs text-white/60">{email}</p>}
                            <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${roleBadgeColors[role] || roleBadgeColors.member}`}>
                                {roleLabels[role] || role}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="p-5">
                    {/* Scope */}
                    <div className={`mb-4 px-3.5 py-2.5 rounded-xl text-xs font-medium flex items-center gap-2.5 ${
                        scopeTier === 'full' ? 'bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/50' :
                        scopeTier === 'org' ? 'bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50' :
                        'bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50'
                    }`}>
                        <Globe size={15} />
                        <div>
                            <div className="font-bold">
                                {scopeTier === 'full' ? 'Unrestricted Access' :
                                 scopeTier === 'org' ? 'Organization Scoped' : 'App Scoped'}
                            </div>
                            <div className="text-[10px] opacity-70 mt-0.5">
                                {scopeTier === 'full' ? 'All organizations and applications' :
                                 scopeTier === 'org' ? `${allowedOrgIds?.length ?? 0} organization${(allowedOrgIds?.length ?? 0) !== 1 ? 's' : ''} — all apps within` :
                                 `${allowedOrgIds?.length ?? 0} org${(allowedOrgIds?.length ?? 0) !== 1 ? 's' : ''}, ${allowedAppIds?.length ?? 0} app${(allowedAppIds?.length ?? 0) !== 1 ? 's' : ''}`}
                            </div>
                        </div>
                    </div>

                    {/* Permissions grid */}
                    <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Module Permissions</div>
                    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                        {PERM_MODULES.map((mod, i) => {
                            const actions = mod.actions || (mod.readOnly ? ['read'] : ['read', 'write']);
                            const scopedModules = ['apps', 'agents', 'pipelines'];
                            const isScoped = scopedModules.includes(mod.id) && scopeTier !== 'full';
                            const noneGranted = actions.every(a => !perms[`perm_${mod.id}_${a}`]);

                            return (
                                <div key={mod.id} className={`flex items-center px-3.5 py-2 ${i < PERM_MODULES.length - 1 ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200 flex-1">{mod.label}</span>
                                    {isScoped && !noneGranted && (
                                        <span className="text-[8px] font-bold text-amber-500 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded mr-2">SCOPED</span>
                                    )}
                                    <div className="flex items-center gap-1">
                                        {actions.map(action => {
                                            const has = perms[`perm_${mod.id}_${action}`];
                                            const actionLabels = { read: 'Read', write: 'Write', create: 'Create', update: 'Update' };
                                            return (
                                                <span key={action}
                                                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                                        has
                                                            ? isScoped
                                                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                                                                : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                                            : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
                                                    }`}
                                                    title={`${actionLabels[action] || action}: ${has ? 'Granted' : 'Denied'}`}
                                                >
                                                    {actionLabels[action] || action[0].toUpperCase()}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Legend */}
                    <div className="mt-3 flex items-center gap-4 text-[10px] text-slate-400">
                        <span className="flex items-center gap-1"><span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[8px] font-medium">Read</span> Granted</span>
                        <span className="flex items-center gap-1"><span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[8px] font-medium">Read</span> Scoped</span>
                        <span className="flex items-center gap-1"><span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 text-[8px] font-medium">Read</span> Denied</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Header;
